import React, { useMemo } from 'react';
import { Box, Grid, Typography, Card, CardContent } from '@mui/material';
import {
  TrendingUp,
  Home,
  People,
  PersonOutline,
  Dashboard as DashboardIcon,
} from '@mui/icons-material';
import MapView from '../../components/Map/MapView';
import PageHeader from '../../components/Common/PageHeader';
import { useAuth } from '../../contexts/AuthContext';
import { useProperties, useClients, useAgents } from '../../hooks/useApiCache';
import { Role, Property } from '../../types';

interface Stats {
  properties: number;
  agents: number;
  clients: number;
  pendingProperties: number;
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();

  // Fetch data using React Query
  const { data: properties = [], isLoading: propertiesLoading } = useProperties();
  const { data: clients = [], isLoading: clientsLoading } = useClients();
  const { data: agents = [], isLoading: agentsLoading } = useAgents();

  // Calculate stats from cached data
  const stats = useMemo<Stats>(() => {
    const props = properties ?? [];
    const agts = agents ?? [];
    const clnts = clients ?? [];
    const pendingCount = props.filter((p: Property) => p.status === 'PENDING').length;

    return {
      properties: props.length,
      agents: agts.length || 0,
      clients: clnts.length,
      pendingProperties: pendingCount,
    };
  }, [properties, agents, clients]);

  const statCards = [
    {
      title: 'Total Properties',
      value: stats.properties,
      icon: <Home sx={{ fontSize: 40, color: 'primary.main' }} />,
      color: '#1976d2',
    },
    ...(user?.role === Role.SUPER_ADMIN
      ? [
          {
            title: 'Total Agents',
            value: stats.agents,
            icon: <People sx={{ fontSize: 40, color: 'success.main' }} />,
            color: '#2e7d32',
          },
        ]
      : []),
    {
      title: 'Total Clients',
      value: stats.clients,
      icon: <PersonOutline sx={{ fontSize: 40, color: 'warning.main' }} />,
      color: '#ed6c02',
    },
    {
      title: 'Pending Approvals',
      value: stats.pendingProperties,
      icon: <TrendingUp sx={{ fontSize: 40, color: 'error.main' }} />,
      color: '#d32f2f',
    },
  ];

  const loading = propertiesLoading || clientsLoading || agentsLoading;

  return (
    <Box sx={{ width: '100%' }}>
      <PageHeader
        title={`Welcome back, ${user?.firstName}!`}
        subtitle="Here's your property management overview"
        icon={<DashboardIcon sx={{ fontSize: 'inherit', color: 'primary.main' }} />}
        breadcrumbs={[
          { label: 'Dashboard' },
        ]}
      />

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <CardContent sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box
                    sx={{
                      padding: '12px',
                      borderRadius: 2,
                      backgroundColor: `${card.color}15`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {card.icon}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {card.title}
                    </Typography>
                    <Typography
                      variant="h5"
                      fontWeight={700}
                      sx={{ color: card.color }}
                    >
                      {loading ? '...' : card.value}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card
        sx={{
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" gutterBottom fontWeight={700} sx={{ mb: 2 }}>
            📍 Properties Map View
          </Typography>
          <MapView />
        </Box>
      </Card>
    </Box>
  );
};

export default Dashboard;
